// /scada/sko — СКО/ОСР command centre (R3). Inbox of curtailment commands
// (auto from lms.ua.energy + manual) and a fast capture form for ОСР orders
// received by email/phone. Mock data; actions update local state only.
import { useEffect, useMemo, useState } from 'react';
import ScadaLayout from '@/components/feature/ScadaLayout';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';
import { STATIONS } from '@/scada/mock/stations';
import { useAllowedStationIds } from '@/scada/useScopedStations';
import { mockSkoCommands, type SkoCommand, type CmdStatus, type CmdSource, type OsrEntry } from '@/scada/mock/events';

const SRC: Record<CmdSource, { label: string; icon: string; c: string }> = {
  lms: { label: 'lms.ua.energy', icon: 'ri-global-line', c: scada.state.ok },
  email: { label: 'Пошта', icon: 'ri-mail-line', c: scada.state.curtailed },
  phone: { label: 'Телефон', icon: 'ri-phone-line', c: scada.state.warning },
};
const CST: Record<CmdStatus, { label: string; c: string }> = {
  pending: { label: 'Нова — потребує підтвердження', c: scada.state.warning },
  acknowledged: { label: 'Підтверджено', c: scada.state.curtailed },
  executing: { label: 'Виконується', c: scada.state.curtailed },
  completed: { label: 'Завершено', c: scada.state.ok },
  expired: { label: 'Протерміновано', c: scada.state.offline },
};
const fmtTime = (s: string) => new Date(s).toLocaleString('uk-UA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });

export default function ScadaSkoPage() {
  const [cmds, setCmds] = useState<SkoCommand[]>(() => mockSkoCommands());
  const [osr, setOsr] = useState<OsrEntry[]>([]);
  const [form, setForm] = useState({ stationName: STATIONS[0].name, source: 'phone' as CmdSource, fromPerson: '', limitPct: '25', reason: 'СКО', comment: '' });
  const [saved, setSaved] = useState(false);
  const allowed = useAllowedStationIds();
  const scopedStations = allowed ? STATIONS.filter((s) => allowed.has(s.id)) : STATIONS;
  useEffect(() => { if (allowed && scopedStations[0] && !scopedStations.some((s) => s.name === form.stationName)) setForm((f) => ({ ...f, stationName: scopedStations[0].name })); }, [allowed]);

  const setStatus = (id: string, status: CmdStatus) => setCmds((c) => c.map((x) => x.id === id ? { ...x, status } : x));

  const pendingCount = useMemo(() => cmds.filter((c) => c.status === 'pending').length, [cmds]);

  const submitOsr = () => {
    if (!form.fromPerson.trim()) return;
    const entry: OsrEntry = {
      id: `osr-${Date.now()}`,
      ts: new Date().toISOString(),
      stationName: form.stationName,
      source: form.source,
      fromPerson: form.fromPerson.trim(),
      limitPct: Number(form.limitPct),
      reason: form.reason,
      comment: form.comment.trim() || undefined,
    };
    setOsr((l) => [entry, ...l]);
    setForm((f) => ({ ...f, fromPerson: '', comment: '' }));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const inp = { background: scada.bg, border: `1px solid ${scada.border}`, color: scada.text, borderRadius: 6, padding: '7px 9px', fontSize: 13, width: '100%' } as const;
  const lbl = { color: scada.textMuted, fontSize: 11, marginBottom: 3, display: 'block' } as const;

  return (
    <ScadaLayout active="sko">
      {/* Inbox */}
      <SectionCard
        title="Команди на обмеження потужності"
        subtitle="Автоматично з системи Укренерго + зафіксовані вручну"
        right={pendingCount > 0 ? <span style={{ background: `${scada.state.warning}1A`, color: scada.state.warning, border: `1px solid ${scada.state.warning}40`, borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{pendingCount} нові</span> : undefined}
        padded={false}
      >
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
            <thead>
              <tr style={{ background: scada.panel2 }}>
                {['Джерело', 'Тип', 'Станція', 'Ліміт', 'Отримано', 'Статус', 'Дія'].map((h) => (
                  <th key={h} style={{ textAlign: 'left', color: scada.textMuted, fontSize: 11, fontWeight: 600, padding: '8px 12px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cmds.map((c) => {
                const s = SRC[c.source];
                return (
                  <tr key={c.id} style={{ borderTop: `1px solid ${scada.border}` }}>
                    <td style={{ padding: '10px 12px' }}>
                      <span className="inline-flex items-center gap-1.5" style={{ color: s.c, fontSize: 12 }}><i className={s.icon} />{s.label}</span>
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{ color: c.kind === 'op_safety' ? scada.state.alarm : scada.textDim, fontSize: 12 }}>
                        {c.kind === 'op_safety' ? 'Опер. безпека' : 'СКО'}
                      </span>
                      {c.compensated != null && <div style={{ fontSize: 10, color: scada.textMuted }}>{c.compensated ? 'компенсується' : 'без компенсації'}</div>}
                    </td>
                    <td style={{ padding: '10px 12px', color: scada.text, fontSize: 13, fontWeight: 600 }}>{c.stationName}{c.docRef && <div style={{ fontSize: 10, color: scada.textMuted, fontWeight: 400 }}>{c.docRef}</div>}</td>
                    <td style={{ padding: '10px 12px', color: scada.state.curtailed, fontSize: 13, fontWeight: 600 }}>{c.limitPct}%</td>
                    <td style={{ padding: '10px 12px', color: scada.textDim, fontSize: 12 }}>{fmtTime(c.receivedAt)}</td>
                    <td style={{ padding: '10px 12px' }}><span style={{ color: CST[c.status].c, fontSize: 12 }}>{CST[c.status].label}</span></td>
                    <td style={{ padding: '10px 12px' }}>
                      <div className="flex gap-1">
                        {c.status === 'pending' && (
                          <button onClick={() => setStatus(c.id, 'executing')} style={{ background: scada.accent, color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>Підтвердити</button>
                        )}
                        {c.status === 'executing' && (
                          <button onClick={() => setStatus(c.id, 'completed')} style={{ background: 'transparent', color: scada.state.ok, border: `1px solid ${scada.state.ok}55`, borderRadius: 6, padding: '5px 12px', fontSize: 12, cursor: 'pointer' }}>Завершити</button>
                        )}
                        {(c.status === 'completed' || c.status === 'expired') && <span style={{ color: scada.textMuted, fontSize: 12 }}>—</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" style={{ marginTop: 16 }}>
        {/* Quick ОСР capture form */}
        <SectionCard title="Зафіксувати команду (ОСР / телефон / лист)" subtitle="Швидке внесення розпорядження, отриманого поза системою">
          <div className="grid grid-cols-2 gap-3">
            <div><label style={lbl}>Станція</label>
              <select value={form.stationName} onChange={(e) => setForm({ ...form, stationName: e.target.value })} style={inp}>
                {scopedStations.map((s) => <option key={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div><label style={lbl}>Джерело</label>
              <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value as CmdSource })} style={inp}>
                <option value="phone">Телефон</option>
                <option value="email">Пошта</option>
                <option value="lms">lms.ua.energy</option>
              </select>
            </div>
            <div><label style={lbl}>Від кого (ПІБ / орган)</label>
              <input value={form.fromPerson} onChange={(e) => setForm({ ...form, fromPerson: e.target.value })} placeholder="напр. диспетчер РДЦ" style={inp} />
            </div>
            <div><label style={lbl}>Ліміт, %</label>
              <input type="number" min={0} max={100} value={form.limitPct} onChange={(e) => setForm({ ...form, limitPct: e.target.value })} style={inp} />
            </div>
            <div><label style={lbl}>Тип</label>
              <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} style={inp}>
                <option>СКО</option>
                <option>Операційна безпека</option>
                <option>Інше</option>
              </select>
            </div>
            <div><label style={lbl}>Коментар</label>
              <input value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="необовʼязково" style={inp} />
            </div>
          </div>
          <div className="flex items-center gap-3" style={{ marginTop: 12 }}>
            <button onClick={submitOsr} disabled={!form.fromPerson.trim()}
              style={{ background: form.fromPerson.trim() ? scada.accent : scada.panel2, color: form.fromPerson.trim() ? '#fff' : scada.textMuted, border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: form.fromPerson.trim() ? 'pointer' : 'not-allowed' }}>
              Зафіксувати команду
            </button>
            {saved && <span style={{ color: scada.state.ok, fontSize: 12 }}><i className="ri-check-line" /> Збережено в журнал</span>}
          </div>
        </SectionCard>

        {/* ОСР log */}
        <SectionCard title="Журнал зафіксованих команд" subtitle={`${osr.length} записів за сесію`}>
          {osr.length === 0 ? (
            <span style={{ color: scada.textMuted, fontSize: 12 }}>Поки немає зафіксованих вручну команд. Заповніть форму ліворуч.</span>
          ) : (
            osr.map((o) => (
              <div key={o.id} style={{ borderBottom: `1px solid ${scada.border}`, padding: '8px 0' }}>
                <div className="flex items-center justify-between">
                  <span style={{ color: scada.text, fontSize: 13, fontWeight: 600 }}>{o.stationName} · {o.limitPct}%</span>
                  <span style={{ color: scada.textMuted, fontSize: 11 }}>{fmtTime(o.ts)}</span>
                </div>
                <div style={{ color: scada.textDim, fontSize: 12 }}>
                  {o.reason} · <i className={SRC[o.source].icon} /> {SRC[o.source].label} · від: {o.fromPerson}
                  {o.comment && <span style={{ color: scada.textMuted }}> — {o.comment}</span>}
                </div>
              </div>
            ))
          )}
        </SectionCard>
      </div>
    </ScadaLayout>
  );
}
